import { Group, createStyles, keyframes } from '@mantine/core';
import { animation } from '@repo/theme/primitives';
import { ThinkingText } from './ThinkingText';

const fadeUp = keyframes({
	'0%': {
		opacity: 0,
		transform: 'translateY(24px)',
	},
	'100%': {
		opacity: 1,
		transform: 'translateY(0)',
	},
});

const fadeUpAnimation = animation(
	fadeUp,
	'duration-300',
	'ease',
	{ opacity: 0, transform: 'translateY(24px)' },
	'forwards'
);

export interface MessageAILoadingProps {
	steps?: { title?: string }[];
}

const useStyles = createStyles((theme) => ({
	root: {
		width: '100%',
		overflow: 'hidden',
	},
	wrapper: {
		position: 'relative',
		height: theme.other.space[6],
		...fadeUpAnimation,
	},
	text: {
		position: 'absolute',
		whiteSpace: 'nowrap',
		top: '0px',
		left: '0px',
		animationFillMode: 'forwards',
		animationDuration: '1s',

		'@keyframes flipUp': {
			from: {
				transform: 'translateY(100%)',
			},
			to: {
				transform: 'translateY(0)',
			},
		},

		'@keyframes flipOut': {
			from: {
				transform: 'translateY(0)',
			},
			to: {
				transform: 'translateY(-100%)',
			},
		},

		'&.last-message': {
			animationName: 'flipUp',
		},

		'&.previous-message': {
			animationName: 'flipOut',
		},
	},
}));

export function MessageAILoading({ steps = [] }: MessageAILoadingProps) {
	const { classes, cx } = useStyles();

	const stepTitles =
		steps?.map((step) => step.title ?? '')?.filter(Boolean) ?? [];
	const messages = ['Thinking', ...stepTitles];

	return (
		<Group spacing="3xs" className={classes.root}>
			<div className={classes.wrapper}>
				{messages.map((message, idx) => {
					let className = 'skip-animation';

					if (messages.length > 1) {
						className = 'previous-message';

						if (idx === messages.length - 1) {
							className = 'last-message';
						}
					}

					return (
						<ThinkingText
							// eslint-disable-next-line react/no-array-index-key
							key={idx}
							size="md"
							color="text/secondary/default"
							className={cx(classes.text, className)}
						>
							{message.endsWith('.')
								? message.substring(0, message.length - 1)
								: message}
						</ThinkingText>
					);
				})}
			</div>
		</Group>
	);
}
